import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Box,
  Grid,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Typography,
  Chip,
  Tabs,
  Tab,
  Skeleton,
  Button,
  IconButton,
} from "@mui/material";
import {
  MapPin,
  BedDouble,
  Bath,
  Maximize,
  Star,
  Eye,
  ArrowRight,
  Building,
  Search,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import instance, { baseUrlWithoutApi } from "../data/BaseUrl";

/**
 * Fetch featured properties as a simple array.
 * @returns {Promise<Array>}
 */
const fetchRecentFromAPI = async () => {
  const response = await instance.get("/recents/featured", {
    params: { per_page: 1000 },
  });
  const recentsData = response.data.data;

  return recentsData.map((recent) => ({
    _id: recent.id,
    image: `${baseUrlWithoutApi}${recent.cover}`,
    beds: recent.bedrooms,
    baths: recent.bathrooms,
    sqft: recent.property_area,
    arabic: {
      title: recent.name,
      location: recent.location,
      price: recent.price,
      type: recent.type,
      availability: recent.category,
    },
    english: {
      title: recent.name_en,
      location: recent.location_en,
      price: recent.price_en,
      type: recent.type_en,
      availability: recent.category_en,
    },
  }));
};

const PropertyCard = ({ property }) => {
  const lang = localStorage.getItem("language") || "arabic";
  const [hovered, setHovered] = useState(false);
  const [favorite, setFavorite] = useState(false);
  const toggleFav = (e) => {
    e.stopPropagation();
    setFavorite((prev) => !prev);
  };
  const p = property[lang];

  return (
    <motion.div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      whileHover={{ y: -4 }}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
    >
      <Link
        to={{
          pathname: `/real-estate/view/${property._id}`,
          state: { property },
        }}
      >
        <Card
          sx={{
            position: "relative",
            borderRadius: 2,
            boxShadow: 2,
            width: "100%",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            fontFamily: '"Alexandria", sans-serif',
          }}
        >
          <Box sx={{ position: "relative", pt: "56.25%" }}>
            <CardMedia
              component="img"
              image={property.image}
              alt={property.title}
              loading="lazy"
              decoding="async"
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
            />
            <Box
              sx={{
                position: "absolute",
                top: 8,
                left: 8,
                display: "flex",
                gap: 0.5,
                zIndex: 3,
              }}
            >
              <Chip
                label={p.type}
                size="small"
                color="primary"
                sx={{
                  fontFamily: '"Alexandria", sans-serif',
                  lineHeight: 2,
                  fontSize: 10,
                }}
              />
              <Chip
                label={p.availability}
                size="small"
                color={
                  p.availability === "Rent" || p.availability === "إيجار"
                    ? "success"
                    : "secondary"
                }
                sx={{
                  fontFamily: '"Alexandria", sans-serif',
                  lineHeight: 2,
                  fontSize: 10,
                }}
              />
            </Box>
            <Box
              onClick={toggleFav}
              sx={{
                position: "absolute",
                top: 8,
                right: 8,
                zIndex: 3,
                bgcolor: "#FFD700", // ذهبية
                borderRadius: 1, // حواف مدورة خفيفة
                px: 0.8, // padding أفقي
                py: 0.4, // padding عمودي
                display: "flex",
                alignItems: "center",
                cursor: "pointer",
                "&:hover": {
                  bgcolor: "#FFC200", 
                },
              }}
            >
              <Star size={16} color="#fff" />
              <Typography
                variant="caption"
                component="span"
                sx={{
                  ml: 0.5,
                  color: "#fff",
                  fontWeight: 700,
                  fontFamily: '"Alexandria", sans-serif',
                  fontSize: 10,
                }}
              >
                {lang === "arabic" ? "مميز" : "Featured"}
              </Typography>
            </Box>
            <AnimatePresence>
              {hovered && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  style={{
                    position: "absolute",
                    inset: 0,
                    backgroundColor: "rgba(0,0,0,0.4)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Button
                    variant="contained"
                    endIcon={<Eye size={14} />}
                    sx={{
                      p: 1.5,
                      fontSize: 11,
                      fontFamily: '"Alexandria", sans-serif',
                      textTransform: "none",
                      flexDirection: "row-reverse",
                      "& .MuiButton-endIcon": { marginLeft: 1, marginRight: 1 },
                    }}
                  >
                    {lang === "arabic" ? "التفاصيل" : "Details"}
                  </Button>
                </motion.div>
              )}
            </AnimatePresence>
          </Box>

          <CardContent sx={{ flexGrow: 1, py: 2, px: 2 }}>
            <Typography
              variant="subtitle1"
              noWrap
              sx={{
                fontFamily: '"Alexandria", sans-serif',
                fontWeight: 600,
              }}
            >
              {p.title}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", mt: 0.5 }}>
              <MapPin size={16} color="rgb(59 130 246)" />
              <Typography
                variant="caption"
                sx={{ fontFamily: '"Alexandria", sans-serif', ml: 0.5 }}
                noWrap
              >
                {p.location}
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                mt: 1.5,
                fontSize: 12,
                color: "text.secondary",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.3 }}>
                <BedDouble size={18} color="rgb(59 130 246)" /> {property.beds}
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.3 }}>
                <Bath size={18} color="rgb(59 130 246)" /> {property.baths}
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.3 }}>
                <Maximize size={18} color="rgb(59 130 246)" /> {property.sqft}
              </Box>
            </Box>
          </CardContent>

          <CardActions sx={{ px: 2, py: 2 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                flexGrow: 1,
              }}
            >
              <Typography
                variant="subtitle2"
                color="primary"
                className="icon-saudi_riyal"
                sx={{
                  fontFamily: '"Alexandria", sans-serif',
                  fontWeight: 700,
                }}
              >
                {p.price}
              </Typography>
            </Box>
            <Chip
              icon={<Building size={13} color="rgb(59 130 246)" />}
              label={p.availability}
              size="small"
              sx={{
                bgcolor: "rgba(25, 118, 210, 0.08)",
                color: "rgb(25 118 210)",
                borderRadius: "10px",
                fontFamily: '"Alexandria", sans-serif',
                fontSize: 10,
                lineHeight: 2,
                p: 2,
              }}
            />
          </CardActions>
        </Card>
      </Link>
    </motion.div>
  );
};
PropertyCard.propTypes = { property: PropTypes.object.isRequired };

const PropertiesShow = () => {
  const lang = localStorage.getItem("language") || "arabic";
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState("all");
  const [page, setPage] = useState(0);

  useEffect(() => {
    setLoading(true);
    fetchRecentFromAPI()
      .then((data) => setProperties(data))
      .catch(() => setError("Failed to fetch, showing demo"))
      .finally(() => setLoading(false));
  }, []);

  const allLabel = lang === "arabic" ? "الكل" : "All";
  const typeValues = Array.from(
    new Set(properties.map((p) => p.english.type.toLowerCase()))
  );
  const categories = [
    { value: "all", label: allLabel },
    ...typeValues.map((typeVal) => {
      const match = properties.find(
        (p) => p.english.type.toLowerCase() === typeVal
      );
      return {
        value: typeVal,
        label: lang === "arabic" ? match.arabic.type : match.english.type,
      };
    }),
  ];

  const filtered =
    tab === "all"
      ? properties
      : properties.filter((p) => p.english.type.toLowerCase() === tab);

  const pageSize = 6;
  const pageCount = Math.ceil(filtered.length / pageSize);
  const current = filtered.slice(page * pageSize, (page + 1) * pageSize);

  return (
    <Box
      component="section"
      sx={{
        py: 6,
        px: { xs: 2, md: 0 },
        maxWidth: 1200,
        mx: "auto",
        overflow: "hidden",
        fontFamily: '"Alexandria", sans-serif',
      }}
    >
      <Box textAlign="center" mb={4}>
        <Typography
          variant="overline"
          color="primary"
          sx={{ fontFamily: '"Alexandria", sans-serif' }}
        >
          {lang === "arabic" ? "استكشف" : "Explore"}
        </Typography>
        <Typography
          variant="h4"
          sx={{
            mt: 1,
            fontFamily: '"Alexandria", sans-serif',
            fontWeight: 700,
          }}
        >
          {lang === "arabic" ? "العقارات المميزة" : "Featured Properties"}
        </Typography>
        <Box
          sx={{
            width: 32,
            height: 4,
            bgcolor: "primary.main",
            mx: "auto",
            mt: 1,
            mb: 2,
          }}
        />
      </Box>

      <Tabs
        value={tab}
        onChange={(e, v) => {
          setTab(v);
          setPage(0);
        }}
        variant="scrollable"
        scrollButtons="on"
        allowScrollButtonsMobile
        dir={lang === "arabic" ? "rtl" : "ltr"}
        sx={{
          mb: 4,
          minHeight: 32,

          "& .MuiTabs-scrollButtons": {
            display: "flex !important",
          },

          "& .MuiTabs-flexContainer": {
            justifyContent: {
              xs: "flex-start",
              sm: "center",
            },
          },

          "& .MuiTabs-scroller": {
            overflowX: "auto",
          },
        }}
        TabIndicatorProps={{ style: { height: 3 } }}
      >
        {categories.map((cat) => (
          <Tab
            key={cat.value}
            label={cat.label}
            value={cat.value}
            sx={{
              fontSize: 12,
              minWidth: 72,
              fontFamily: '"Alexandria", sans-serif',
            }}
          />
        ))}
      </Tabs>

      {loading ? (
        <Grid container spacing={2}>
          {[...Array(3)].map((_, i) => (
            <Grid item xs={12} sm={6} md={4} key={i}>
              <Skeleton variant="rounded" height={280} />
            </Grid>
          ))}
        </Grid>
      ) : (
        <>
          {error && (
            <Typography
              color="warning.main"
              align="center"
              mb={2}
              variant="caption"
            >
              {error}
            </Typography>
          )}

          {current.length ? (
            <Grid container spacing={2}>
              {current.map((prop) => (
                <Grid item xs={12} sm={6} md={4} key={prop._id}>
                  <PropertyCard property={prop} />
                </Grid>
              ))}
            </Grid>
          ) : (
            <Box textAlign="center" py={4}>
              <Search size={40} color="#aaa" />
              <Typography
                variant="subtitle1"
                mt={2}
                sx={{ fontFamily: '"Alexandria", sans-serif' }}
              >
                {lang === "arabic"
                  ? "لا توجد عقارات"
                  : "No properties available"}
              </Typography>
              <Button
                onClick={() => {
                  setTab("all");
                  setPage(0);
                }}
                sx={{ mt: 1, fontSize: 12 }}
              >
                {allLabel}
              </Button>
            </Box>
          )}

          {pageCount > 1 && (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              mt={3}
              gap={1}
            >
              <IconButton
                disabled={page === 0}
                onClick={() => setPage((p) => Math.max(p - 1, 0))}
              >
                {lang !== "arabic" ? <ChevronLeft /> : <ChevronRight />}
              </IconButton>
              <Typography>
                {page + 1} / {pageCount}
              </Typography>
              <IconButton
                disabled={page === pageCount - 1}
                onClick={() => setPage((p) => Math.min(p + 1, pageCount - 1))}
              >
                {lang === "arabic" ? <ChevronLeft /> : <ChevronRight />}
              </IconButton>
            </Box>
          )}
        </>
      )}

      {/* <Box textAlign="center" mt={5}>
        <Button
          endIcon={<ArrowRight />}
          size="small"
          variant="contained"
          sx={{ fontSize: 12 }}
        >
          {lang === "arabic" ? "جميع العقارات" : "Browse All"}
        </Button>
      </Box> */}
    </Box>
  );
};

export default PropertiesShow;
